import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Play, 
  Pause, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  MapPin, 
  Server,
  Laptop,
  Monitor
} from 'lucide-react';
import http from '../../lib/api';

export default function PlanificadorGlobal() {
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState('activo'); // activo | pausado
  const [motivoPausa, setMotivoPausa] = useState('');
  const [generationResult, setGenerationResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemStatus();
  }, []);

  const fetchSystemStatus = async () => {
    try {
      setStatusLoading(true);
      const res = await http.get('/ia-agendamiento-global/config/estado');
      setSystemStatus(res.data.estado);
      setMotivoPausa(res.data.motivo_pausa || '');
    } catch (err) {
      console.error("Error fetching status:", err);
      // Si falla, asumimos activo pero mostramos error discreto
      // setError("No se pudo obtener el estado del sistema.");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleTogglePause = async () => {
    try {
      setLoading(true);
      const nuevoEstado = systemStatus === 'activo'; // Si está activo, vamos a pausar
      const motivo = nuevoEstado ? prompt("Ingrese el motivo de la pausa:") : "";
      
      if (nuevoEstado && !motivo) {
        setLoading(false);
        return; // Cancelar si no hay motivo al pausar
      }

      await http.post('/ia-agendamiento-global/config/pausa', {
        pausado: nuevoEstado,
        motivo: motivo || "Reanudado por administrador"
      });
      
      setSystemStatus(nuevoEstado ? 'pausado' : 'activo');
      setMotivoPausa(motivo);
    } catch (err) {
      setError("Error al cambiar el estado del sistema: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      setGenerationResult(null);
      
      const res = await http.post('/ia-agendamiento-global/generar-global');
      
      if (res.data.mensaje && res.data.mensaje.includes("pausado")) {
        setError(res.data.mensaje);
      } else {
        setGenerationResult(res.data);
      }
    } catch (err) {
      console.error("Error generating calendar:", err);
      setError(err.response?.data?.detail || "Error generando el calendario global.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Calendar className="w-8 h-8 text-emerald-600" />
            Planificador Global IA
          </h1>
          <p className="text-slate-500 mt-1">
            Gestión centralizada de visitas para toda la flota técnica.
          </p>
        </div>
        
        {/* Status Control */}
        <div className={`flex items-center gap-4 px-4 py-2 rounded-lg border shadow-sm transition-colors ${
          systemStatus === 'activo' 
            ? 'bg-green-50 border-green-200 text-green-700' 
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          <div className="flex items-center gap-2 font-medium">
            {systemStatus === 'activo' ? (
              <><CheckCircle size={20} /> Sistema Activo</>
            ) : (
              <><Pause size={20} /> Sistema Pausado</>
            )}
          </div>
          <button
            onClick={handleTogglePause}
            disabled={loading || statusLoading}
            className={`px-3 py-1 rounded-md text-sm font-bold transition-all hover:shadow-md ${
              systemStatus === 'activo'
                ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            }`}
          >
            {systemStatus === 'activo' ? 'Pausar' : 'Reanudar'}
          </button>
        </div>
      </div>

      {/* Main Action Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -mr-10 -mt-10 opacity-50 pointer-events-none"></div>
          
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2 relative z-10">
            <Server className="w-5 h-5 text-teal-600" />
            Generación de Calendario
          </h2>
          
          <div className="prose prose-slate text-slate-600 mb-6 relative z-10">
            <p>
              Este proceso utiliza Inteligencia Artificial para optimizar la agenda completa:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Analiza la demanda de <strong>todos los clientes</strong> simultáneamente.</li>
              <li>Calcula tiempos reales: <strong>Notebooks (~25m)</strong> vs <strong>PCs (~45m)</strong>.</li>
              <li>Agrupa visitas por <strong>proximidad geográfica</strong> (Tetris Geográfico).</li>
              <li>Distribuye la carga <strong>50/50 entre quincenas</strong>.</li>
            </ul>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 animate-in slide-in-from-top-2">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Atención</p>
                <p>{error}</p>
              </div>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={loading || systemStatus === 'pausado'}
            className={`w-full py-4 rounded-xl flex items-center justify-center gap-3 text-lg font-bold transition-all shadow-md relative z-10
              ${loading 
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                : systemStatus === 'pausado'
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]'
              }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-current"></div>
                Procesando Demanda Global...
              </>
            ) : (
              <>
                <Play className="w-6 h-6 fill-current" />
                Generar Calendario Mensual
              </>
            )}
          </button>
          
          {systemStatus === 'pausado' && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg text-amber-800 text-sm flex items-center gap-2">
              <AlertTriangle size={16} />
              <span>
                <strong>Sistema Pausado:</strong> {motivoPausa || "Sin motivo especificado"}
              </span>
            </div>
          )}
        </div>

        {/* Stats / Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Métricas de Optimización</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="p-2 bg-emerald-100 rounded-full text-emerald-600">
                  <Laptop size={20} />
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-bold uppercase">Notebooks</p>
                  <p className="text-sm text-slate-600">Estimación: ~25 min</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-100">
                <div className="p-2 bg-teal-100 rounded-full text-teal-600">
                  <Monitor size={20} />
                </div>
                <div>
                  <p className="text-xs text-teal-600 font-bold uppercase">PCs Escritorio</p>
                  <p className="text-sm text-slate-600">Estimación: ~45 min</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="p-2 bg-emerald-100 rounded-full text-emerald-600">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="text-xs text-emerald-600 font-bold uppercase">Rutas</p>
                  <p className="text-sm text-slate-600">Agrupación inteligente</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-400 text-center">
              Motor IA v2.0 - Powered by Elements
            </p>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {generationResult && (
        <div className="bg-white rounded-xl shadow-lg border border-green-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-green-50 px-6 py-4 border-b border-green-100 flex items-center justify-between">
            <h3 className="text-lg font-bold text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Generación Exitosa
            </h3>
            <span className="text-sm text-green-600 font-mono">
              {new Date().toLocaleString()}
            </span>
          </div>
          
          <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-4 rounded-lg hover:bg-slate-50 transition-colors">
              <p className="text-4xl font-bold text-slate-800 mb-1">
                {generationResult.total_visitas}
              </p>
              <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                Visitas Calculadas
              </p>
            </div>
            
            <div className="p-4 rounded-lg hover:bg-slate-50 transition-colors md:border-x border-slate-100">
              <p className="text-4xl font-bold text-emerald-600 mb-1">
                {generationResult.dias_ocupados}
              </p>
              <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                Días Ocupados
              </p>
            </div>
            
            <div className="p-4 rounded-lg hover:bg-slate-50 transition-colors">
              <p className="text-4xl font-bold text-emerald-600 mb-1">
                {generationResult.agendamientos_creados}
              </p>
              <p className="text-sm text-slate-500 uppercase tracking-wide font-semibold">
                Agendamientos Creados
              </p>
            </div>
          </div>
          
          <div className="bg-slate-50 px-6 py-4 text-center border-t border-slate-100">
            <p className="text-slate-600">
              El calendario ha sido actualizado. Puedes ver los detalles en la sección <strong>Agendamiento</strong>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}